import React, { useMemo } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useTranslation } from 'react-i18next';
import { ReactQueryProvider } from '@/providers/react-query-provider';
import { Tabs } from '@/components/design-system/molecules/tabs';
import { ToastProvider } from '@/providers/toast-provider';
import {
  AdminRoutes,
  AdminSideNavigation,
} from '@/features/admin/navigation/containers/admin-side-navigation';
import { RoomAssignmentList } from '@/features/admin/contracts/components/room-assignment-list';
import { AuthContextProvider } from '@/hooks/use-auth';
import { DiscussionThreadsProvider } from '@/features/shared/discussion-threads/providers';
import { NotificationsProvider } from '@/features/shared/notifications/provider';
import Contracts from '@/features/admin/contracts';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import AddColocForm from '@/features/admin/contracts/components/contracts/components/add-coloc-form/AddColocForm';
import PickRoomForm from '@/features/admin/contracts/components/contracts/components/pick-room-form/PickRoomForm';
import PickDatesForm from '@/features/admin/contracts/components/contracts/components/pick-dates-form/PickDatesForm';
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  Navigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import ConfirmationDialog from '@/features/admin/contracts/components/contracts/components/confirmation-dialog/ConfirmationDialog';
import SubscriptionSuccessModal from '@/components/SubscriptionSuccessModal';
import { useContractWizard } from '@/features/admin/contracts/hooks/useContractWizard';
import SuccessModal from '@/components/SuccessModal';

// Types for the different forms
interface ColocFormData {
  colocFirstName: string;
  colocLastName: string;
  colocEmail: string;
  colocMobileCountryCode: string;
  colocMobileNumber: string;
  colocLocale: string;
  colocId: string;
  securityDepositAmount: string;
  securityDepositAmountPaid: string;
}

interface RoomFormData {
  houseId: string;
  roomId: string;
  accessCode?: string;
}

const TABS = [
  {
    value: 'contracts',
    label: 'js.features.admin.contracts.contracts_page_tabs.all_contracts',
    Component: Contracts,
  },
  {
    value: 'room_assignment',
    label: 'js.features.admin.contracts.contracts_page_tabs.room_assignment',
    Component: RoomAssignmentList,
  },
];

const ContractContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const tabIndex = TABS.findIndex((t) => t.value === tab);
    return tabIndex !== -1 ? tabIndex : 0;
  }, [location.search]);

  const handleTabChange = (index: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', TABS[Number(index)].value);
    setSearchParams(newParams);
  };

  return (
    <div className="flex h-screen w-screen">
      <AdminSideNavigation tabToSet={AdminRoutes.Contracts} />
      <div className="flex h-full flex-1 flex-col overflow-hidden px-7 pt-3">
        <div className="flex items-center justify-between pb-3">
          <h1 className="text-h3 font-bold text-text-primary">
            {t('js.features.admin.contracts.title')}
          </h1>
          <button
            className="flex h-[40px] items-center justify-center rounded-[8px] bg-R300 px-[18px] text-body font-medium text-text-primary hover:bg-R400"
            onClick={() => navigate('/admin/contract-wizard/add-coloc')}
          >
            <IconSaxIcon name="add" size={20} strokeWidth={1.5} className="mr-2" />
            Create Contract
          </button>
        </div>
        <div className="flex w-full flex-1 flex-col overflow-hidden bg-E10">
          <Tabs selectedIndex={selectedTab} onChange={(index) => handleTabChange(Number(index))}>
            <Tabs.TabsList>
              {TABS.map((tab) => (
                <Tabs.Tab key={tab.value}>
                  <Tabs.TabDefaultContent>{t(tab.label) ?? ''}</Tabs.TabDefaultContent>
                </Tabs.Tab>
              ))}
            </Tabs.TabsList>
            <Tabs.TabsScreens>
              {TABS.map((tab) => (
                <Tabs.TabScreen key={tab.value}>
                  <tab.Component />
                </Tabs.TabScreen>
              ))}
            </Tabs.TabsScreens>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

// Contract wizard component that manages the three-step process
const ContractWizard = () => {
  const {
    step,
    formState,
    isCreating,
    showSuccessModal,
    showingConfirmation,
    handleColocSubmit,
    handleRoomSubmit,
    handleDatesSubmit,
    safeOnClose,
    safePrevious,
    handleSuccessModalClose,
    cancelNavigation,
    confirmNavigation,
  } = useContractWizard();

  // Render the appropriate step based on the route parameter
  let currentStep;
  switch (step) {
    case 'add-coloc':
      currentStep = (
        <AddColocForm
          isOpen={true}
          onClose={safeOnClose}
          onSubmit={handleColocSubmit}
          initialData={formState.colocData}
        />
      );
      break;
    case 'pick-room':
      currentStep = (
        <PickRoomForm
          isOpen={true}
          onClose={safeOnClose}
          onPrevious={safePrevious}
          onSubmit={handleRoomSubmit}
          initialData={formState.roomData}
        />
      );
      break;
    case 'pick-dates':
      currentStep = (
        <PickDatesForm
          isOpen={true}
          onClose={safeOnClose}
          onPrevious={safePrevious}
          onSubmit={handleDatesSubmit}
          isSubmitting={isCreating}
          initialData={formState.datesData}
        />
      );
      break;
    default:
      currentStep = <Navigate to="/admin/contract-wizard/add-coloc" replace />;
  }

  return (
    <>
      {currentStep}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showingConfirmation}
        onCancel={cancelNavigation}
        onConfirm={confirmNavigation}
        title="Are you sure you want to cancel?"
        message="All unsaved details will be discarded."
        confirmLabel="Discard and Exit"
        cancelLabel="Keep Creating"
      />

      <SuccessModal
        isOpen={showSuccessModal}
        title="Created successfully"
        description="Contract details were added and the contract was created."
        onClose={handleSuccessModalClose}
      />
    </>
  );
};

// Main component that sets up all the providers
const ContractsScreen = () => {
  return (
    <ReactQueryProvider>
      <ToastProvider />
      <BrowserRouter>
        <AuthContextProvider>
          <DiscussionThreadsProvider>
            <NotificationsProvider>
              <ReactQueryDevtools initialIsOpen={false} />
              <Routes>
                <Route path="/" element={<Navigate to="/admin/contracts" replace />} />
                <Route path="/admin/contracts/*" element={<ContractContent />} />
                <Route path="/admin/contract-wizard/*" element={<ContractWizard />} />
                <Route path="*" element={<Navigate to="/admin/contracts" replace />} />
              </Routes>
            </NotificationsProvider>
          </DiscussionThreadsProvider>
        </AuthContextProvider>
      </BrowserRouter>
    </ReactQueryProvider>
  );
};

export { ContractsScreen };
